/**
 * Shared timeline calculation utility
 * Matches the backend logic for consistency
 */

export interface EventSchedule {
  invitationCloseDaysBeforeEvent: number;
  invitationCloseTime?: string;           // HH:mm format, default: 23:59
  mealAssignmentDaysBeforeEvent: number;
  mealAssignmentTime?: string;            // HH:mm format, default: 10:00
  appetizerDuration: number;
  mainCourseDuration: number;
  dessertDuration: number;
  firstStopEmailMinutesBefore?: number;   // Minutes before event to send first email, default: 30
}

export interface TimelineData {
  eventDate: string;
  eventTime: string;
  invitationCloseDate: string;
  invitationCloseTime: string;
  mealAssignmentDate: string;
  mealAssignmentTime: string;
  firstMessageTime: string;
  starterTime: string;
  mainCourseMessageTime: string;
  mainCourseTime: string;
  dessertMessageTime: string;
  dessertTime: string;
  finalPartyTime: string;
}

const formatNorwegianDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatNorwegianTime = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const calculateTimeline = (
  scheduledDate: string,
  schedule: EventSchedule
): TimelineData => {
  const eventDate = new Date(scheduledDate);

  // Pre-event dates
  const invitationCloseDate = new Date(eventDate);
  invitationCloseDate.setDate(invitationCloseDate.getDate() - schedule.invitationCloseDaysBeforeEvent);
  // Parse invitationCloseTime (HH:mm)
  const [inviteHours, inviteMinutes] = (schedule.invitationCloseTime || '23:59').split(':').map(Number);
  invitationCloseDate.setHours(inviteHours, inviteMinutes, 0, 0);

  const mealAssignmentDate = new Date(eventDate);
  mealAssignmentDate.setDate(mealAssignmentDate.getDate() - schedule.mealAssignmentDaysBeforeEvent);
  // Parse mealAssignmentTime (HH:mm)
  const [mealHours, mealMinutes] = (schedule.mealAssignmentTime || '10:00').split(':').map(Number);
  mealAssignmentDate.setHours(mealHours, mealMinutes, 0, 0);

  // Event day timeline
  const starterTime = new Date(eventDate.getTime());
  const minutesBefore = schedule.firstStopEmailMinutesBefore || 30;
  const firstMessageTime = new Date(eventDate.getTime() - minutesBefore * 60000);
  const mainCourseMessageTime = new Date(eventDate.getTime() + (schedule.appetizerDuration / 2) * 60000);
  const mainCourseTime = new Date(eventDate.getTime() + schedule.appetizerDuration * 60000);
  const dessertMessageTime = new Date(mainCourseTime.getTime() + (schedule.mainCourseDuration / 2) * 60000);
  const dessertTime = new Date(mainCourseTime.getTime() + schedule.mainCourseDuration * 60000);
  const finalPartyTime = new Date(dessertTime.getTime() + schedule.dessertDuration * 60000);

  return {
    eventDate: formatNorwegianDate(eventDate),
    eventTime: formatNorwegianTime(eventDate),
    invitationCloseDate: formatNorwegianDate(invitationCloseDate),
    invitationCloseTime: formatNorwegianTime(invitationCloseDate),
    mealAssignmentDate: formatNorwegianDate(mealAssignmentDate),
    mealAssignmentTime: formatNorwegianTime(mealAssignmentDate),
    firstMessageTime: formatNorwegianTime(firstMessageTime),
    starterTime: formatNorwegianTime(starterTime),
    mainCourseMessageTime: formatNorwegianTime(mainCourseMessageTime),
    mainCourseTime: formatNorwegianTime(mainCourseTime),
    dessertMessageTime: formatNorwegianTime(dessertMessageTime),
    dessertTime: formatNorwegianTime(dessertTime),
    finalPartyTime: formatNorwegianTime(finalPartyTime),
  };
};
